<template>
  <focus-lock>
    <el-dialog
      ref="eventRequestDialog"
      v-loading="loading"
      custom-class="feedback-modal"
      :show-close="false"
      :fullscreen="true"
      :visible.sync="dialogVisible"
      :before-close="onClose"
      @opened="dialogOpen('eventRequestDialog')"
    >
      <div class="container header">
        <el-row>
          <el-col>
            <div class="d-flex">
              <h2 class="text-break">
                New event request
              </h2>
              <button
                type="button"
                aria-label="Close"
                class="close ml-auto"
                @click="onClose"
              >
                <i class="el-dialog__close el-icon el-icon-close"></i>
              </button>
            </div>
          </el-col>
        </el-row>
      </div>

      <div class="review-section container">
        <el-row>
          <el-col
            :xs="24"
            :sm="18"
            :md="14"
          >
            <h2>Request details</h2>
            <p class="el-form-item__info">
              Please complete the form to provide information about your event.
              The Community Wealth Builder team will review your request and
              publish your event.
            </p>
          </el-col>
        </el-row>
        <el-row v-if="validationErrors.length">
          <el-col
            :xs="24"
            :sm="18"
            :md="14"
          >
            <validation-errors :errors="validationErrors"></validation-errors>
          </el-col>
        </el-row>
        <el-row>
          <el-col
            :xs="24"
            :sm="18"
            :md="14"
          >
            <el-form
              id="eventRequestForm"
              ref="eventRequestForm"
              :model="eventRequest.form"
              :rules="eventRequest.rules"
              autocomplete="false"
              @submit.native.prevent
            >
              <!-- Start title -->
              <el-form-item
                prop="title"
                :error="eventRequestError"
              >
                <template slot="label">
                  Event title
                </template>
                <el-input
                  id="feedback"
                  v-model="eventRequest.form.title"
                  placeholder
                  :maxlength="50"
                  label="Please enter the event title"
                  aria-required="true"
                />
              </el-form-item>
              <!-- End title -->
              <!-- Start description -->
              <el-form-item
                prop="description"
                :error="eventRequestError"
              >
                <template slot="label">
                  Event description
                </template>
                <el-input
                  id="description"
                  v-model="eventRequest.form.description"
                  placeholder
                  type="textarea"
                  :rows="5"
                  maxlength="1000"
                  aria-required="true"
                ></el-input>
              </el-form-item>
              <div class="mt-n3 mb-4">
                <span class="se-body-m-screen color-light-gray">You have
                  {{ 1000 - eventRequest.form.description.length }} characters
                  remaining</span>
              </div>
              <!-- End description -->
              <!-- Start Event Type -->
              <el-form-item
                prop="type"
                :error="eventRequestError"
              >
                <template slot="label">
                  Event type
                </template>
                <v-select
                  v-model="eventRequest.form.type"
                  :value="eventRequest.form.type"
                  :options="eventRequest.types"
                  :searchable="false"
                  :clearable="false"
                  :reduce="(type) => type.key"
                  autocomplete="false"
                  class="half-content"
                />
              </el-form-item>
              <!-- End Event Type -->
              <!-- Start Address/Url Event -->
              <div v-if="eventRequest.form.type == 'event'">
                <address-selector
                  :required="true"
                  :address="eventRequest.form.address"
                  @update="($event) => (eventRequest.form.address = $event)"
                />
              </div>
              <el-form-item
                v-else
                prop="event_url"
                :error="eventRequestError"
              >
                <template slot="label">
                  URL
                </template>
                <el-input
                  id="event_url"
                  v-model="eventRequest.form.event_url"
                  placeholder
                  maxlength="100"
                  aria-required="true"
                ></el-input>
              </el-form-item>
              <!-- End Address/Url Event -->
              <!-- Start Start date/time -->
              <el-row>
                <el-col
                  :xs="24"
                  :sm="12"
                >
                  <el-form-item
                    prop="start_date"
                    :error="eventRequestError"
                  >
                    <template slot="label">
                      Start Date
                    </template>
                    <el-date-picker
                      v-model="eventRequest.form.start_date"
                      type="date"
                      placeholder="Pick a day"
                      format="dd/MM/yyyy"
                      value-format="yyyy-MM-dd"
                      name="Start date"
                      :editable="false"
                    />
                  </el-form-item>
                </el-col>
                <el-col
                  :xs="24"
                  :sm="12"
                >
                  <el-form-item
                    prop="start_time"
                    :error="eventRequestError"
                  >
                    <template slot="label">
                      Start Time
                    </template>
                    <el-time-select
                      v-model="eventRequest.form.start_time"
                      :picker-options="{
                        start: '00:00',
                        step: '00:15',
                        end: '23:45',
                      }"
                      placeholder="Pick a time"
                      name="Start time"
                    />
                  </el-form-item>
                </el-col>
              </el-row>
              
              <!-- End Start date/time -->
              <!-- Start End date/time -->
              <el-row>
                <el-col
                  :xs="24"
                  :sm="12"
                >
                  <el-form-item
                    prop="end_date"
                    :error="eventRequestError"
                  >
                    <template slot="label">
                      End Date
                    </template>
                    <el-date-picker
                      v-model="eventRequest.form.end_date"
                      type="date"
                      placeholder="Pick a day"
                      format="dd/MM/yyyy"
                      value-format="yyyy-MM-dd"
                      name="End date"
                      :editable="false"
                    />
                  </el-form-item>
                </el-col>
                <el-col
                  :xs="24"
                  :sm="12"
                >
                  <el-form-item
                    prop="end_time"
                    :error="eventRequestError"
                  >
                    <template slot="label">
                      End Time
                    </template>
                    <el-time-select
                      v-model="eventRequest.form.end_time"
                      :picker-options="{
                        start: '00:00',
                        step: '00:15',
                        end: '23:45',
                      }"
                      placeholder="Pick a time"
                      name="End time"
                    />
                  </el-form-item>
                </el-col>
              </el-row>
              
              <!-- End End date/time -->
              <!-- Start Additional information -->
              <el-form-item
                prop="additional_info"
                :error="eventRequestError"
              >
                <template slot="label">
                  Additional information (optional)
                </template>
                <el-input
                  id="additional_info"
                  v-model="eventRequest.form.additional_info"
                  placeholder="Please enter additional information here such as, if it's a recurring event, when it recurs and until when? And whether you have a publication date for this event"
                  type="textarea"
                  :rows="5"
                  maxlength="1000"
                  aria-required="true"
                ></el-input>
              </el-form-item>
              <div class="mt-n3 mb-4">
                <span class="se-body-m-screen color-light-gray">You have
                  {{
                    1000 - eventRequest.form.additional_info.length
                  }}
                  characters remaining</span>
              </div>
              <!-- Start Additional information -->
              <!-- Start Files uploader -->
              <el-form-item
                v-loading="removeFilesLoader"
                class="form-group"
                prop="image"
                element-loading-background="rgba(255, 255, 255, 0.8)"
              >
                <template slot="label">
                  Event attachment(s) (optional)
                </template>
                <el-upload
                  action=""
                  :auto-upload="false"         
                  multiple
                  class="multiple-uploader"
                  :on-change="onFileChange"
                >
                  <el-button
                    size="small"
                    type="primary"
                  >
                    Click to upload
                  </el-button>
                  <div
                    slot="tip"
                    class="el-upload__tip"
                  >
                    Max file size 10MB (PDF, DOCX, JPEG, PNG)
                  </div>
                </el-upload>
              </el-form-item>
              <!-- End Files uploader -->
              <!-- Start Services -->
              <el-form-item
                id="services"
                prop="services"
                class="is-no-asterisk form-group w-100"
                required
              >
                <template slot="label">
                  <span aria-hidden="true">Services</span><span class="is-custom-asterisk">*</span><br />
                  <p class="el-form-item__info">
                    (You can select multiple options)
                  </p>
                  <span class="sr-only">Please select one or more services</span>
                  <span
                    class="sr-only"
                    aria-live="polite"
                    :hidden="!hasInlineValidationErrorFor('services')"
                    v-text="getInlineValidationErrorFor('services')"
                  ></span>
                </template>
                <el-checkbox-group v-model="eventRequest.form.services">
                  <el-row>
                    <el-col
                      v-for="service in services"
                      :key="service.id"
                      :md="24"
                      :lg="12"
                      class="mb-3"
                    >
                      <el-checkbox
                        name="edit_service_id"
                        :label="service.id"
                      >
                        {{ service.name }}
                      </el-checkbox>
                    </el-col>
                  </el-row>
                </el-checkbox-group>
              </el-form-item>
              <!-- End Services -->
              <!-- Start Localities -->
              <el-form-item
                id="localities"
                prop="localities"
                class="is-no-asterisk form-group w-100"
              >
                <template slot="label">
                  <span aria-hidden="true">Localities</span><span class="is-custom-asterisk">*</span><br />
                  <p class="mt-3 mb-0">
                    Which regions of Doncaster is your event available in?
                  </p>
                  <p class="el-form-item__info mt-0">
                    (You can select multiple options)
                  </p>
                  <span class="sr-only">Please select one or more localities</span>
                  <span
                    class="sr-only"
                    aria-live="polite"
                    :hidden="!hasInlineValidationErrorFor('localities')"
                    v-text="getInlineValidationErrorFor('localities')"
                  ></span>
                </template>
                <el-checkbox-group v-model="eventRequest.form.localities">
                  <el-row>
                    <el-col
                      v-for="locality in localities"
                      :key="locality.id"
                      class="mb-3"
                    >
                      <el-checkbox
                        name="edit_locality_id"
                        :label="locality.id"
                      >
                        {{ locality.name }}
                      </el-checkbox>
                    </el-col>
                  </el-row>
                </el-checkbox-group>
              </el-form-item>
              <!-- End Localities -->

              <div class="d-flex">
                <el-button
                  class="mt-3 mb-3 submit-btn"
                  type="primary"
                  @click="submitEventRequest"
                >
                  Submit request
                </el-button>
                <el-button
                  class="mt-3 mb-3"
                  type="secondary"
                  @click="onClose"
                >
                  Cancel
                </el-button>
              </div>
            </el-form>
          </el-col>
        </el-row>
      </div>
    </el-dialog>
  </focus-lock>
</template>

<script>
import gsApi from "@/services/gs.api";
import validationErrors from "@/components/partials/validation-errors";
import { validateWebsiteAddress } from "@/helpers/validators";
import AddressSelector from "@/components/partials/address-selector";
import inlineValidationErrorsMixin from "@/mixins/inlineValidationErrorsMixin";
export default {
  components: {
    validationErrors,
    AddressSelector,
  },
  mixins: [inlineValidationErrorsMixin],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    services: {
      type: Array,
      default: () => [],
    },
    localities: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      validationErrors: [],
      loading: false,
      eventRequest: {
        form: {
          title: "",
          description: "",
          type: "event",
          address: {
            address_1: "",
            address_2: "",
            post_code: "",
            town: "",
            county: "",
          },
          localities: [],
          start_date: null,
          start_time: null,
          end_date: null,
          end_time: null,
          additional_info: "",
          services: [],
          event_url: null,
          attachments:[]
        },
        rules: {
          title: [
            {
              required: true,
              message: "Enter the event title",
              trigger: "blur",
            },
            {
              max: 50,
              message: "title may not be greater than 50 characters",
              trigger: "blur",
            },
          ],
          description: [
            {
              required: true,
              message: "Enter the event description",
              trigger: "blur",
            },
            {
              max: 1000,
              message: "Description may not be greater than 1000 characters",
              trigger: "blur",
            },
          ],
          event_url: [{ required: true, validator: validateWebsiteAddress }],
          services: [
            {
              type: "array",
              required: true,
              message: "Select at least one service",
              trigger: "change",
            },
          ],
          localities: [
            {
              type: "array",
              required: true,
              message: "Select at least one locality",
              trigger: "change",
            },
          ],
          'address.address_1': [
            {
              required: true,
              message: "Enter the address line 1",
            },
            {
              max: 255,
              message: "address line 1 may not be greater than 255 characters",
            },
          ],
          'address.town': [
            { required: true, message: "Enter the town"},
            {
              max: 255,
              message: "town may not be greater than 255 characters",
            },
          ],
          start_date: [
            {
              required: true,
              message: "Select a start date",
              trigger: "change",
            },
          ],
          start_time: [
            {
              required: true,
              message: "Select a start time",
              trigger: "change",
            },
          ],
          end_date: [
            {
              required: true,
              message: "Select an end date",
              trigger: "change",
            },
          ],
          end_time: [
            {
              required: true,
              message: "Select an end time",
              trigger: "change",
            },
          ],
        },
        types: [
          {
            label: "Event",
            key: "event",
          },
          {
            label: "Online",
            key: "online",
          },
        ],
      },
      eventRequestError: null,
      dialogVisible: false,
      removeFilesLoader: false,
      cropperFile: null,
      showCropperDialog: false,
      eventFile: null,
      customAvatar: false,
    };
  },
  watch: {
    visible() {
      this.dialogVisible = this.visible;
    },
  },
  created() {
    this.dialogVisible = this.visible;
    this.initializeForm();
  },
  methods: {
    initializeForm() {
      this.eventRequest.form = {
        title: null,
        description: "",
        type: "event",
        localities: [],
        start_date: null,
        end_date: null,
        additional_info: "",
        services: [],
        event_url: null,
        address: {
          address_1: "",
          address_2: "",
          post_code: "",
          town: "",
          county: "",
        },
        attachments:[]
      };

      if (this.$refs.eventRequestForm) {

        this.$refs.eventRequestForm.resetFields();
        this.$refs.eventRequestForm.clearValidate();
        this.validationErrors = [];
      }
    },
    // Start of upload / cropper methods
    onFileChange(file, fileList) {
      this.eventRequest.form.attachments = fileList.map(f => f.raw);

    },
    // End of upload / cropper methods

    submitEventRequest() {
      this.$refs.eventRequestForm.validate((valid) => {
        if (valid) {

          this.loading = true;
          const formData = new FormData();
          formData.append(
            "title",
            this.$options.filters.domPurify(this.eventRequest.form.title, {
              ALLOWED_TAGS: [],
            })
          );
          formData.append(
            "description",
            this.$options.filters.domPurify(
              this.eventRequest.form.description,
              { ALLOWED_TAGS: [] }
            )
          );
          formData.append("type", this.eventRequest.form.type);
          formData.append(
            "additional_info",
            this.$options.filters.domPurify(
              this.eventRequest.form.additional_info,
              { ALLOWED_TAGS: [] }
            )
          );
          formData.append("start_date", this.eventRequest.form.start_date);
          formData.append("end_date", this.eventRequest.form.end_date);
          formData.append("start_time", this.eventRequest.form.start_time);
          formData.append("end_time", this.eventRequest.form.end_time);

          if (this.eventRequest.form.type == "event") {
            formData.append(
              "post_code",
              this.eventRequest.form.address.post_code
            );
            formData.append(
              "address_1",
              this.eventRequest.form.address.address_1
            );
            formData.append(
              "address_2",
              this.eventRequest.form.address.address_2
            );
            formData.append("town", this.eventRequest.form.address.town);
            formData.append("county", this.eventRequest.form.address.county);
          } else {
            formData.append(
              "url",
              this.$options.filters.domPurify(
                this.eventRequest.form.event_url,
                { ALLOWED_TAGS: [] }
              )
            );
          }

          this.eventRequest.form.attachments.forEach((attachment) => {
            formData.append('attachments[]', attachment)
          })

          this.eventRequest.form.services.forEach((service) => {
            formData.append("services[]", service);
          });

          this.eventRequest.form.localities.forEach((locality) => {
            formData.append("localities[]", locality);
          });

          gsApi.events
            .submitRequest(formData)
            .then(() => {
              this.validationErrors = [];
              this.onClose();
              this.$alert('Your Event has been submitted to the CWB team to review. If they have any questions, they will contact you. Otherwise, your event will soon be published to the site.', 
                'Success', { confirmButtonText: 'Close', showClose: false, customClass: 'notification-modal' });
            })
            .catch((error) => {
              this.validationErrors = [];

              if (
                error.response &&
                error.response.status == 422 &&
                error.response.data
              ) {
                let errors = error.response.data.errors;
                for (let [key, value] of Object.entries(errors)) {
                  let obj = {};
                  obj.id = key;
                  obj.message = value[0];
                  this.validationErrors.push(obj);
                }
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                this.$alert(
                  "There are errors in the form, please fix these errors and try again.",
                  "Error",
                  {
                    confirmButtonText: "Close",
                    showClose: false,
                    customClass: "notification-modal",
                  }
                );
              }
            })
            .finally(() => {
              this.loading = false;
            });
        } else {
          this.validationErrors = [];
          this.$refs.eventRequestForm.fields.forEach((field) => {
            if (field.validateState == "error") {
              let obj = {};
              obj.id = field.labelFor;
              obj.message = field.validateMessage;
              this.validationErrors.push(obj);
            }
          });
          this.$nextTick(() => {
            document
              .getElementById("formErrorBlock")
              .scrollIntoView({ behavior: "smooth", block: "start" });
            let text = document
              .getElementById("eventRequestForm")
              .getElementsByClassName("el-form-item__error")[0].textContent;
            this.$announcer.set(text);
          });
        }
      });
    },
    onClose() {
      this.initializeForm();
      this.$emit("close");
    },
  },
};
</script>
